/**
 * OrganizationAPI - Firestore CRUD operations for organizations
 */

import { Logger } from '../utils/Logger';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';

const logger = Logger.getLogger('OrganizationAPI');

export interface Organization {
  id?: string;
  name: string;
  owner: string; // User ID of the owner
  members: string[]; // Array of user IDs
  plan: 'free' | 'pro' | 'enterprise';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface OrganizationMember {
  userId: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  joinedAt: Timestamp;
}

export interface OrganizationInvitation {
  id: string;
  orgId: string;
  email: string;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: Timestamp;
  expiresAt: Timestamp;
}

/**
 * Create a new organization
 */
export async function createOrganization(
  ownerId: string,
  name: string
): Promise<string> {
  try {
    const orgsRef = collection(db, 'organizations');
    const newDocRef = doc(orgsRef); // Auto-generate ID
    const now = Timestamp.now();

    const orgData: Omit<Organization, 'id'> = {
      name,
      owner: ownerId,
      members: [ownerId], // Owner is automatically a member
      plan: 'free',
      createdAt: now,
      updatedAt: now
    };

    await setDoc(newDocRef, orgData);
    logger.info('Organization created:', newDocRef.id);
    return newDocRef.id;
  } catch (error) {
    logger.error('Error creating organization:', error);
    throw new Error('Failed to create organization');
  }
}

/**
 * Get an organization by ID
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (orgSnap.exists()) {
      return {
        id: orgSnap.id,
        ...orgSnap.data()
      } as Organization;
    } else {
      logger.warn('Organization not found:', orgId);
      return null;
    }
  } catch (error) {
    logger.error('Error getting organization:', error);
    throw new Error('Failed to get organization');
  }
}

/**
 * Get all organizations for a user
 */
export async function listUserOrganizations(userId: string): Promise<Organization[]> {
  try {
    const orgsRef = collection(db, 'organizations');
    const q = query(orgsRef, where('members', 'array-contains', userId));
    const querySnapshot = await getDocs(q);

    const organizations: Organization[] = [];
    querySnapshot.forEach((doc) => {
      organizations.push({
        id: doc.id,
        ...doc.data()
      } as Organization);
    });

    logger.info(`Found ${organizations.length} organizations for user ${userId}`);
    return organizations;
  } catch (error) {
    logger.error('Error listing organizations:', error);
    throw new Error('Failed to list organizations');
  }
}

/**
 * Update an organization
 */
export async function updateOrganization(
  orgId: string,
  updates: Partial<Omit<Organization, 'id' | 'owner' | 'createdAt'>>
): Promise<void> {
  try {
    const orgRef = doc(db, 'organizations', orgId);

    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    await updateDoc(orgRef, updateData);
    logger.info('Organization updated:', orgId);
  } catch (error) {
    logger.error('Error updating organization:', error);
    throw new Error('Failed to update organization');
  }
}

/**
 * Delete an organization
 */
export async function deleteOrganization(orgId: string): Promise<void> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await deleteDoc(orgRef);
    logger.info('Organization deleted:', orgId);
  } catch (error) {
    logger.error('Error deleting organization:', error);
    throw new Error('Failed to delete organization');
  }
}

/**
 * Add a member to an organization
 */
export async function addMember(orgId: string, userId: string): Promise<void> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      members: arrayUnion(userId),
      updatedAt: Timestamp.now()
    });
    logger.info('Member added to organization:', userId);
  } catch (error) {
    logger.error('Error adding member:', error);
    throw new Error('Failed to add member');
  }
}

/**
 * Remove a member from an organization
 */
export async function removeMember(orgId: string, userId: string): Promise<void> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      members: arrayRemove(userId),
      updatedAt: Timestamp.now()
    });
    logger.info('Member removed from organization:', userId);
  } catch (error) {
    logger.error('Error removing member:', error);
    throw new Error('Failed to remove member');
  }
}

/**
 * Check if user is a member of an organization
 */
export async function isMemberOfOrg(userId: string, orgId: string): Promise<boolean> {
  try {
    const org = await getOrganization(orgId);
    return org ? org.members.includes(userId) : false;
  } catch (error) {
    logger.error('Error checking membership:', error);
    return false;
  }
}

/**
 * Check if user is the owner of an organization
 */
export async function isOwnerOfOrg(userId: string, orgId: string): Promise<boolean> {
  try {
    const org = await getOrganization(orgId);
    return org ? org.owner === userId : false;
  } catch (error) {
    logger.error('Error checking ownership:', error);
    return false;
  }
}

/**
 * Invite a user to an organization by email
 * This creates an invitation that the user can accept
 */
export async function inviteMember(
  orgId: string,
  email: string,
  invitedBy: string
): Promise<string> {
  try {
    const invitesRef = collection(db, 'invitations');

    // Check for existing pending invitation to prevent race condition duplicates
    const existingQuery = query(
      invitesRef,
      where('orgId', '==', orgId),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error('An invitation has already been sent to this email');
    }

    const newInviteRef = doc(invitesRef);
    const now = Timestamp.now();

    const inviteData = {
      orgId,
      email: email.toLowerCase(),
      invitedBy,
      status: 'pending', // pending, accepted, declined
      createdAt: now,
      expiresAt: Timestamp.fromMillis(now.toMillis() + 7 * 24 * 60 * 60 * 1000) // 7 days
    };

    await setDoc(newInviteRef, inviteData);
    logger.info('Invitation created:', newInviteRef.id);
    return newInviteRef.id;
  } catch (error) {
    logger.error('Error creating invitation:', error);
    throw new Error('Failed to create invitation');
  }
}

/**
 * Get pending invitations for a user's email
 */
export async function getPendingInvitations(email: string): Promise<OrganizationInvitation[]> {
  try {
    const invitesRef = collection(db, 'invitations');
    const q = query(
      invitesRef,
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);

    const invitations: OrganizationInvitation[] = [];
    querySnapshot.forEach((docSnapshot) => {
      invitations.push({
        id: docSnapshot.id,
        ...docSnapshot.data()
      } as OrganizationInvitation);
    });

    return invitations;
  } catch (error) {
    logger.error('Error getting invitations:', error);
    return [];
  }
}

/**
 * Accept an invitation (via secure Cloud Function)
 */
export async function acceptInvitation(invitationId: string): Promise<void> {
  try {
    // Call the Cloud Function to securely accept the invitation
    const acceptInvitationFn = httpsCallable(functions, 'acceptInvitation');
    const result = await acceptInvitationFn({ invitationId });

    const data = result.data as { success: boolean; message: string; orgId?: string; orgName?: string };

    if (data.success) {
      logger.info('Invitation accepted:', data.message);
      if (data.orgName) {
        logger.info(`Joined organization: ${data.orgName}`);
      }
    } else {
      throw new Error(data.message || 'Failed to accept invitation');
    }
  } catch (error) {
    logger.error('Error accepting invitation:', error);

    // Extract error message from Firebase Functions error
    const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation';
    throw new Error(errorMessage);
  }
}

/**
 * Decline an invitation (via secure Cloud Function)
 */
export async function declineInvitation(invitationId: string): Promise<void> {
  try {
    // Call the Cloud Function to securely decline the invitation
    const declineInvitationFn = httpsCallable(functions, 'declineInvitation');
    const result = await declineInvitationFn({ invitationId });

    const data = result.data as { success: boolean; message: string };

    if (data.success) {
      logger.info('Invitation declined:', data.message);
    } else {
      throw new Error(data.message || 'Failed to decline invitation');
    }
  } catch (error) {
    logger.error('Error declining invitation:', error);

    // Extract error message from Firebase Functions error
    const errorMessage = error instanceof Error ? error.message : 'Failed to decline invitation';
    throw new Error(errorMessage);
  }
}

